import axios from "axios";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

export const SLOW_MSG = "Stiamo elaborando la tua fattura, l'operazione richiederà pochi istanti. Non ricaricare la pagina";
const SLOW_ID = "slow-op";
let slowCount = 0;
const endSlow = (cfg) => {
  if (!cfg) return;
  clearTimeout(cfg._slowTimer);
  if (cfg._slow) { cfg._slow = false; slowCount -= 1; if (slowCount <= 0) { slowCount = 0; toast.dismiss(SLOW_ID); } }
};
api.interceptors.request.use((cfg) => {
  cfg._slowTimer = setTimeout(() => {
    cfg._slow = true;
    slowCount += 1;
    toast.loading(SLOW_MSG, { id: SLOW_ID, duration: Infinity });
  }, 5000);
  return cfg;
});

let refreshing = null;
api.interceptors.response.use(
  (r) => { endSlow(r.config); return r; },
  async (err) => {
    endSlow(err.config);
    const orig = err.config || {};
    const skip = /auth\/(login|refresh|logout)/.test(orig.url || "");
    if (err.response?.status === 401 && !orig._retry && !skip) {
      orig._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(orig);
      } catch (e) {
        refreshing = null;
        window.dispatchEvent(new Event("auth:logout"));
      }
    }
    return Promise.reject(err);
  }
);

export function errMsg(e) {
  const st = e?.response?.status;
  const d = e?.response?.data?.detail;
  if ((e?.isAxiosError && !e.response) || ([502, 503, 504].includes(st) && d == null)) return SLOW_MSG;
  if (d == null) return e?.message || "Si è verificato un errore";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && x.msg) || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
}

export async function download(path, params) {
  try {
    const r = await api.get(path, { params, responseType: "blob" });
    const cd = r.headers["content-disposition"] || "";
    const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
    const name = (star && decodeURIComponent(star[1])) || (cd.match(/filename="?([^";]+)"?/) || [])[1] || "documento";
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    if (e?.response?.data instanceof Blob) {
      const t = await e.response.data.text();
      try {
        throw new Error(JSON.parse(t).detail);
      } catch (x) {
        throw new Error(x.message || "Download non riuscito");
      }
    }
    throw new Error(errMsg(e));
  }
}

const euroFmt = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });
export const eur = (n) => euroFmt.format(Number(n) || 0);
export const isProfessionista = (c) => c?.coge_regime === "professionisti" || (c?.coge_regime === "forfettaria" && !!c?.coge_param?.professionista);
export const dt = (s) => (s ? s.slice(0, 10).split("-").reverse().join("/") : "—");
export const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
export const addDays = (iso, d) => {
  const [y, m, g] = String(iso).slice(0, 10).split("-").map(Number);
  const x = new Date(Date.UTC(y, m - 1, g + Number(d || 0)));
  return x.toISOString().slice(0, 10);
};

export const TIPI = {
  TD01: "Fattura", TD24: "Fattura differita (con DDT)", TD25: "Fattura differita triangolazione interna", TD04: "Nota di credito", TD02: "Acconto su fattura",
  TD03: "Acconto su parcella", TD05: "Nota di debito", TD06: "Parcella", TD26: "Cessione beni ammortizzabili e passaggi interni",
  TD16: "Integrazione reverse charge interno", TD17: "Integrazione/autofattura servizi dall'estero", TD18: "Integrazione beni intracomunitari",
  TD19: "Integrazione/autofattura beni art.17 c.2", TD20: "Autofattura regolarizzazione", TD21: "Autofattura per splafonamento",
  TD22: "Estrazione beni da Deposito IVA", TD23: "Estrazione beni da Deposito IVA con versamento IVA",
  TD27: "Autoconsumo o cessioni gratuite", TD28: "Acquisti da San Marino con IVA", TD29: "Comunicazione omessa o irregolare fatturazione",
};
export const TIPI_AUTO = ["TD16", "TD17", "TD18", "TD19", "TD20", "TD22", "TD23", "TD28", "TD29"];
export const TIPI_SELF = ["TD21", "TD27"];
export const EU = ["AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GR", "HR", "HU", "IE", "LT", "LU", "LV", "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK"];
export const isForeign = (naz) => !!naz && naz.toUpperCase() !== "IT";
export const pivaOk = (p) => {
  if (!/^\d{11}$/.test(p || "")) return false;
  let s = 0;
  for (let i = 0; i < 10; i++) { let d = Number(p[i]); if (i % 2) { d *= 2; if (d > 9) d -= 9; } s += d; }
  return (10 - (s % 10)) % 10 === Number(p[10]);
};
const ODD = [1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23];
export const cfOk = (raw) => {
  const cf = (raw || "").toUpperCase();
  if (/^\d{11}$/.test(cf)) return pivaOk(cf);
  if (!/^[A-Z0-9]{16}$/.test(cf)) return false;
  let s = 0;
  for (let i = 0; i < 15; i++) { const ch = cf[i]; const v = /\d/.test(ch) ? Number(ch) : ch.charCodeAt(0) - 65; s += i % 2 === 0 ? ODD[v] : v; }
  return String.fromCharCode(65 + (s % 26)) === cf[15];
};
export const proposedNatura = (naz) => {
  const n = (naz || "IT").toUpperCase();
  if (n === "IT") return null;
  if (n === "SM") return "N3.3";
  return EU.includes(n) ? "N3.2" : "N3.1";
};
export const foreignDefaults = (naz, prev = {}) => {
  const n = (naz || "").toUpperCase();
  if (n === "IT") return { nazione: n, provincia: prev.provincia === "EE" ? "" : prev.provincia, cap: prev.cap === "00000" ? "" : prev.cap, codice_destinatario: ["XXXXXXX", "2R4GTO8"].includes(prev.codice_destinatario) ? "0000000" : prev.codice_destinatario };
  if (n.length !== 2) return { nazione: n };
  return { nazione: n, provincia: "EE", cap: n === "SM" && /^\d{5}$/.test(prev.cap || "") ? prev.cap : "00000", codice_destinatario: n === "SM" ? "2R4GTO8" : "XXXXXXX" };
};
export const CASSE = {
  TC01: "Avvocati", TC02: "Dottori commercialisti", TC03: "Geometri", TC04: "Ingegneri e architetti", TC05: "Notariato", TC06: "Ragionieri",
  TC07: "ENASARCO", TC08: "ENPACL", TC09: "ENPAM", TC10: "ENPAF", TC11: "ENPAV", TC12: "ENPAIA", TC13: "Imprese spedizione", TC14: "INPGI",
  TC15: "ONAOSI", TC16: "CASAGIT", TC17: "EPPI", TC18: "EPAP", TC19: "ENPAB", TC20: "ENPAPI", TC21: "ENPAP", TC22: "INPS",
};
export const RITENUTE = { RT01: "Ritenuta d'acconto persone fisiche", RT02: "Ritenuta d'acconto persone giuridiche", RT03: "Contributo INPS", RT04: "Contributo ENASARCO", RT05: "Contributo ENPAM", RT06: "Altro contributo previdenziale" };
export const CAUSALI_PAG = { A: "A - Lavoro autonomo abituale", B: "B - Diritti d'autore", C: "C - Contratti associazione", M: "M - Lavoro autonomo occasionale", Q: "Q - Agente monomandatario", R: "R - Agente plurimandatario", S: "S - Commissionario", T: "T - Mediatore", U: "U - Procacciatore d'affari", V: "V - Vendita a domicilio", W: "W - Appalti (condominio)", ZO: "ZO - Altro" };
export const DOC_TIPI = { ordine: "Ordine di acquisto", contratto: "Contratto", convenzione: "Convenzione", ricezione: "Ricezione", collegata: "Fattura collegata" };
export const ESIGIBILITA = { I: "Immediata", D: "Differita", S: "Scissione pagamenti (split payment)" };
export const ALIQUOTE = [22, 10, 5, 4, 0];
export const NATURE = {
  "N1": "N1 - Escluse ex art. 15",
  "N2.1": "N2.1 - Non soggette art. 7-7septies",
  "N2.2": "N2.2 - Non soggette (forfettari, altri)",
  "N3.1": "N3.1 - Non imponibili esportazioni",
  "N3.2": "N3.2 - Non imponibili cessioni intracom.",
  "N3.3": "N3.3 - Non imponibili cessioni verso San Marino",
  "N3.4": "N3.4 - Non imponibili assimilate esportazioni",
  "N3.5": "N3.5 - Non imponibili dichiarazioni d'intento",
  "N3.6": "N3.6 - Non imponibili altre operazioni",
  "N4": "N4 - Esenti",
  "N5": "N5 - Regime del margine",
  "N6.1": "N6.1 - Reverse charge rottami",
  "N6.2": "N6.2 - Reverse charge oro e argento",
  "N6.3": "N6.3 - Reverse charge subappalto edilizia",
  "N6.4": "N6.4 - Reverse charge fabbricati",
  "N6.5": "N6.5 - Reverse charge telefoni cellulari",
  "N6.6": "N6.6 - Reverse charge prodotti elettronici",
  "N6.7": "N6.7 - Reverse charge edilizia",
  "N6.8": "N6.8 - Reverse charge settore energetico",
  "N6.9": "N6.9 - Reverse charge altri casi",
  "N7": "N7 - IVA assolta in altro stato UE",
};
export const REGIMI = {
  RF01: "RF01 - Ordinario",
  RF02: "RF02 - Contribuenti minimi",
  RF04: "RF04 - Agricoltura e attività connesse e pesca",
  RF05: "RF05 - Vendita sali e tabacchi",
  RF06: "RF06 - Commercio dei fiammiferi",
  RF07: "RF07 - Editoria",
  RF08: "RF08 - Gestione servizi di telefonia pubblica",
  RF09: "RF09 - Rivendita documenti di trasporto pubblico e di sosta",
  RF10: "RF10 - Intrattenimenti, giochi e altre attività (DPR 640/72)",
  RF11: "RF11 - Agenzie di viaggi e turismo",
  RF12: "RF12 - Agriturismo",
  RF13: "RF13 - Vendite a domicilio",
  RF14: "RF14 - Rivendita beni usati, oggetti d'arte, antiquariato o da collezione",
  RF15: "RF15 - Agenzie di vendite all'asta di oggetti d'arte, antiquariato o da collezione",
  RF16: "RF16 - IVA per cassa P.A.",
  RF17: "RF17 - IVA per cassa (art. 32-bis, D.L. 83/2012)",
  RF18: "RF18 - Altro",
  RF19: "RF19 - Forfettario",
};
export const MODALITA = { MP01: "Contanti", MP02: "Assegno", MP05: "Bonifico", MP08: "Carta di pagamento", MP12: "RIBA", MP19: "SEPA Direct Debit" };
export const RUOLI = { admin: "Amministratore", operatore: "Operatore", lettura: "Sola lettura", agente: "Agente di vendita" };

export function computeTotals(lines, ritenute, bollo, cassa, sconto, split) {
  const r2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;
  const groups = {};
  let imponibile = 0;
  let escluse = 0;
  lines.forEach((l) => {
    const tot = r2((Number(l.quantita) || 0) * (Number(l.prezzo) || 0) * (1 - (Number(l.sconto) || 0) / 100));
    const a = Number(l.aliquota) || 0;
    const key = `${a}|${a === 0 ? l.natura || "N2.2" : ""}`;
    groups[key] = (groups[key] || 0) + tot;
    imponibile += tot;
    if (a === 0 && (l.natura || "N2.2") === "N1") escluse += tot;
  });
  const impRighe = r2(imponibile - escluse);
  const esentiRighe = Object.entries(groups).filter(([k]) => k.startsWith("0|")).reduce((s, [, v]) => s + v, 0);
  const cassaBase = r2(impRighe + (bollo && esentiRighe > 77.47 ? 2 : 0));
  let cassaImp = 0;
  if (cassa?.tipo) {
    cassaImp = r2((cassaBase * (Number(cassa.aliquota) || 0)) / 100);
    const ca = Number(cassa.aliquota_iva) || 0;
    const key = `${ca}|${ca === 0 ? cassa.natura || "N2.2" : ""}`;
    groups[key] = (groups[key] || 0) + cassaImp;
    imponibile += cassaImp;
  }
  const riepilogo = Object.entries(groups).map(([k, v]) => {
    const [a, n] = k.split("|");
    return { aliquota: Number(a), natura: n, imponibile: r2(v), imposta: r2((v * Number(a)) / 100) };
  });
  const iva = r2(riepilogo.reduce((s, r) => s + r.imposta, 0));
  const esenti = riepilogo.filter((r) => r.aliquota === 0).reduce((s, r) => s + r.imponibile, 0);
  const bolloImp = bollo && esenti > 77.47 ? 2 : 0;
  const baseRit = impRighe + (cassa?.ritenuta ? cassaImp : 0);
  const rits = (ritenute || []).filter((r) => Number(r.aliquota) > 0).map((r) => {
    let b = (["RT01", "RT02"].includes(r.tipo) ? baseRit : impRighe) * (Number(r.perc_imponibile) || 100) / 100;
    if (r.base_max != null) b = Math.min(b, Math.max(0, Number(r.base_max)));
    return { ...r, base: b, importo: r2((b * Number(r.aliquota)) / 100) };
  }).filter((r) => r.base_max == null || r.base > 0);
  const ritenuta = r2(rits.reduce((s, r) => s + r.importo, 0));
  const lordo = r2(imponibile + iva + bolloImp);
  let sc = 0;
  if (sconto && ["SC", "MG"].includes(sconto.tipo)) {
    sc = r2(Number(sconto.importo) || (lordo * (Number(sconto.percentuale) || 0)) / 100) * (sconto.tipo === "MG" ? 1 : -1);
  }
  const totale = r2(lordo + sc);
  return { imponibile: r2(imponibile), iva, bollo: bolloImp, cassa: cassaImp, sconto_totale: sc, ritenuta, ritenute: rits, totale, netto: r2(totale - ritenuta - (split ? iva : 0)), riepilogo };
}
